import React, { useCallback, useState } from "react";

import * as yup from "yup";
import { Box, Button, FormControlLabel, Radio, RadioGroup, Stack, TextField, Typography } from "@mui/material";
import { ValidationError } from "components";
import { DataRoomConnected } from "components/modals";
import { HTTP_DATAROOM_2FA_AUTHENTICATOR, HTTP_DATAROOM_2FA_REQUIRED, HTTP_DATAROOM_2FA_SMS } from "constants/http";
import { useFormik } from "formik";
import { useActionHook, useDidUpdate } from "hooks";
import { IApiServiceError, useDataRoomsActions, useDataroomsConnectMsgSelect } from "store";

type Credo = Partial<{
  username: string;
  password: string;
  subdomain: string;
  type: string;
  errors: string;
  authMethod: string;
}>;

interface LoginMobileProps {
  platformName: string;
  platformId: string;
  closeModal: () => void;
  setLoading: (value: boolean) => void;
  setStep: (value: number) => void;
  extra: { [key: string]: string | boolean | null } | null;
  resetSocket: () => void;
  afterSubmit?: (connectionId: number) => void;
}

const defaultValudation = yup.object().shape({
  username: yup.string().required("This field is requeired"),
  password: yup.string().required("This field is requeired")
});

const codeValidation = yup.object().shape({
  code: yup.string().required("This field is requeired")
});

const authenticationMethodValidation = yup.object().shape({});

export const LoginMobile: React.FC<LoginMobileProps> = ({
  platformName,
  platformId,
  closeModal,
  setLoading,
  extra,
  setStep,
  resetSocket,
  afterSubmit
}) => {
  const [credo, setCredo] = useState<Credo | null>(null);
  const [freshConnId, setFreshConnId] = useState<number | null>(null);
  const [successConnId, setSuccessConnId] = useState<number | null>(null);
  const [error, setError] = useState<string>("");
  const [contactInfo, setContactInfo] = useState<string>("");

  const socketData = useDataroomsConnectMsgSelect();

  const { createConnectionAction, resendCodeAction, authenticateConnectionAction, deleteConnection } =
    useDataRoomsActions();

  const { action: resendCode, loading: resendLoading } = useActionHook(resendCodeAction);

  const onCloseWrap = useCallback(() => {
    setCredo(null);
    setFreshConnId(null);
    setSuccessConnId(null);
    setError("");
    closeModal();
    resetSocket();
  }, []);

  useDidUpdate(() => {
    setLoading(resendLoading);
  }, [resendLoading]);

  // Invalidate data if choose platform
  useDidUpdate(() => {
    if (credo) {
      setCredo(null);
    }
  }, [platformId]);

  const { values, handleChange, handleSubmit, errors, isValid } = useFormik({
    initialValues: credo
      ? credo.type == HTTP_DATAROOM_2FA_REQUIRED
        ? {
            authMethod: ""
          }
        : {
            code: ""
          }
      : {
          username: "",
          password: "",
          subdomain: "",
          type: HTTP_DATAROOM_2FA_SMS,
          errors: "",
          authMethod: ""
        },
    validationSchema: credo
      ? credo.type == HTTP_DATAROOM_2FA_REQUIRED
        ? authenticationMethodValidation
        : codeValidation
      : defaultValudation,
    // Submit logic
    onSubmit: async (values) => {
      let connId = null;
      try {
        setLoading(true);
        if (error) {
          setError("");
        }
        if (values.username && values.password) {
          connId = await createConnectionAction({ ...values, type: platformId });
          setFreshConnId(connId);
          await authenticateConnectionAction({ connection_id: connId });
          setSuccessConnId(connId);
        }
        if (values.code && freshConnId && credo) {
          await authenticateConnectionAction({ connection_id: freshConnId, ...values, ...credo });
          setSuccessConnId(freshConnId);
        }
        if (values.authMethod && freshConnId && credo && credo.type == HTTP_DATAROOM_2FA_REQUIRED) {
          await authenticateConnectionAction({ connection_id: freshConnId, ...credo, ...values });
          setSuccessConnId(freshConnId);
        }
        if (afterSubmit && connId) {
          afterSubmit(connId);
        }
      } catch (error) {
        const { message, status, errors } = error as IApiServiceError;
        if (message === "MFA required" && ((values.username && values.password) || values.authMethod)) {
          resetSocket();
          values.type = status?.toString() ?? "";
          values.errors = JSON.stringify(errors);
          setContactInfo((errors as any)?.ref);
          return setCredo(values);
        }
        if (message.startsWith("MFA failed")) {
          values.type = status?.toString() ?? "";
          values.errors = JSON.stringify(errors);
          return setCredo(values);
        }
        if (message) {
          setError(errors?.["message"]?.join("") || message);
          if (errors["connId"]) {
            deleteConnection(parseInt(errors["connId"][0]));
            return setCredo(null);
          } else {
            if (freshConnId || parseInt(String(connId)) > 0) deleteConnection(freshConnId || parseInt(String(connId)));
            return setCredo(null);
          }
        }
      } finally {
        setLoading(false);
      }
    },
    enableReinitialize: true
  });

  return (
    <Box sx={{ width: "100%" }}>
      {credo ? (
        <form onSubmit={handleSubmit}>
          <Stack spacing={2}>
            {(credo.type == HTTP_DATAROOM_2FA_REQUIRED || credo.type == "") && (
              <Box>
                <Typography
                  sx={{
                    fontSize: "16px",
                    color: "#0a3142",
                    lineHeight: "20px",
                    mb: "8px"
                  }}
                >
                  {extra?.["2faMessageStep3"] ||
                    "You will now receive an " +
                      (contactInfo || "SMS to your phone number") +
                      " with a security code from " +
                      platformName +
                      " platform, this might take a couple of minutes."}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "16px",
                    color: "#0a3142",
                    lineHeight: "20px"
                  }}
                >
                  * Code is valid for 5 minutes
                </Typography>
              </Box>
            )}
            {credo.type == HTTP_DATAROOM_2FA_REQUIRED && (
              <Typography
                sx={{
                  fontSize: "16px",
                  color: "#0a3142",
                  lineHeight: "20px",
                  fontWeight: 500
                }}
              >
                Select authentication method:
              </Typography>
            )}
            {credo.type == HTTP_DATAROOM_2FA_REQUIRED && (
              <Typography
                sx={{
                  fontSize: "16px",
                  color: "#0a3142",
                  lineHeight: "20px"
                }}
              >
                {extra?.["2faAuthenticator"] || "Please enter your Authenticator Secret code."}
              </Typography>
            )}
            {credo.type != HTTP_DATAROOM_2FA_REQUIRED && (
              <>
                <TextField
                  error={!!errors.code}
                  helperText={errors.code}
                  value={values.code}
                  onChange={handleChange}
                  name="code"
                  label={credo.type == HTTP_DATAROOM_2FA_AUTHENTICATOR ? "Secret code" : "Verification code"}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  size="medium"
                  placeholder="Enter code"
                />
                {socketData?.message && (
                  <Typography
                    component="span"
                    sx={{
                      fontSize: "14px",
                      color: "#0a3142",
                      lineHeight: "20px"
                    }}
                  >
                    {socketData?.message}
                    <br />
                    Valid for {socketData?.countdown} minutes
                  </Typography>
                )}
              </>
            )}
            {credo.type == HTTP_DATAROOM_2FA_REQUIRED && (
              <Box>
                <RadioGroup onChange={handleChange} name="authMethod">
                  {credo.errors &&
                    JSON.parse(credo.errors).map((factor: any) => {
                      return (
                        <FormControlLabel
                          key={factor.id}
                          value={factor.id}
                          control={<Radio />}
                          label={factor.type + (factor.description ? " - " + factor.description : "")}
                          sx={{
                            mb: "8px",
                            "& .MuiFormControlLabel-label": {
                              fontSize: "16px",
                              color: "#0a3142"
                            }
                          }}
                        />
                      );
                    })}
                </RadioGroup>
              </Box>
            )}
            <ValidationError messages={error || []} />

            <Button
              disabled={!isValid}
              type="submit"
              variant="contained"
              fullWidth
              sx={{
                py: "12px",
                fontSize: "16px",
                fontWeight: 500,
                textTransform: "none",
                mt: "8px"
              }}
            >
              Next
            </Button>
            <Box
              onClick={() =>
                freshConnId &&
                resendCode({
                  connection_id: freshConnId
                })
              }
              sx={{
                cursor: "pointer",
                textDecoration: "underline",
                textAlign: "center",
                mt: "8px"
              }}
            >
              {credo.type != HTTP_DATAROOM_2FA_REQUIRED && credo.type != HTTP_DATAROOM_2FA_AUTHENTICATOR && (
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: "16px",
                    color: "#0a3142"
                  }}
                >
                  Didn&apos;t receive a code? Click here to send again
                </Typography>
              )}
            </Box>
          </Stack>
        </form>
      ) : (
        <form onSubmit={handleSubmit}>
          <Stack gap="20px">
            {(extra?.show2FAMessage || false) && (
              <Typography
                sx={{
                  fontSize: "16px",
                  color: "#0a3142",
                  lineHeight: "20px"
                }}
              >
                {extra?.["2faMessageStep2"] ||
                  "After entering the username and password you will receive an SMS with a code. Please notice that the code is valid for a few minutes only."}
              </Typography>
            )}
            {(extra?.showSubdomain || false) && (
              <TextField
                error={!!errors.subdomain}
                helperText={errors.subdomain}
                value={values.subdomain}
                onChange={handleChange}
                name="subdomain"
                label={extra?.["subDomainTitle"] || "subdomain (if you have)"}
                InputLabelProps={{ shrink: true }}
                fullWidth
                size="medium"
                placeholder={String(extra?.["subDomainPlaceholder"] || "Enter a subdomain")}
              />
            )}
            <TextField
              error={!!errors.username}
              helperText={errors.username}
              value={values.username}
              onChange={handleChange}
              name="username"
              label="Email or Username"
              InputLabelProps={{ shrink: true }}
              fullWidth
              size="medium"
              placeholder="Enter email or username"
            />
            <TextField
              error={!!errors.password}
              value={values.password}
              helperText={errors.password}
              onChange={handleChange}
              name="password"
              label="Password"
              type="password"
              InputLabelProps={{ shrink: true }}
              fullWidth
              size="medium"
              placeholder="Enter password"
            />
            <ValidationError messages={error || []} />
            <Stack direction="row" spacing={2} sx={{ mt: "8px" }}>
              <Button
                type="button"
                variant="outlined"
                onClick={() => setStep(1)}
                fullWidth
                sx={{
                  py: "12px",
                  fontSize: "16px",
                  fontWeight: 500,
                  textTransform: "none"
                }}
              >
                Back
              </Button>
              <Button
                disabled={!isValid}
                type="submit"
                variant="contained"
                fullWidth
                sx={{
                  py: "12px",
                  fontSize: "16px",
                  fontWeight: 500,
                  textTransform: "none"
                }}
              >
                Next
              </Button>
            </Stack>
          </Stack>
        </form>
      )}
      <DataRoomConnected connectionId={successConnId} onClose={onCloseWrap} />
    </Box>
  );
};
